import { ResultOf } from '@graphql-typed-document-node/core'

import { BlockSection } from '@/app/_components/BlockSection/index.jsx'
import { Gutter } from '@/app/_components/Gutter/index.jsx'
import { CreditCardOffer } from '@/app/_components/Offers/CreditCardOffers.jsx'
import { LoanOffer } from '@/app/_components/Offers/LoanOffers.jsx'
import { ProductOffer } from '@/app/_components/Offers/ProductOffers.jsx'
import { SectionHeader } from '@/app/_components/SectionHeader/index.jsx'

import {
  isCreditCardOffer,
  isLoanOffer,
  isPersonalCreditCardOffer,
  isProductOptionOffer,
  Offer,
} from '../../_components/OffersProvider/offers-provider-context.js'
import { useOffer } from '../../_components/OffersProvider/use-offers.js'
import { PreviewWarningBlock } from '../../_components/PreviewWarning/index.jsx'
import { cmsGraphql, FragmentType, getFragmentData } from '../../lib/cms/index.js'
import { isBlockKind } from '../is-block-kind.js'

export const offerBlockTypename = 'CmsOfferBlock'
const offerBlockType = 'offer'

export const offerBlockFragment = cmsGraphql(/* GraphQL */ `
  fragment offerBlockFragment on CmsOfferBlock {
    invertBackground
    section {
      ...cmsSectionFragment
      ...cmsBlockSectionFragment
    }
    ctas {
      link {
        ...cmsLinkFragment
      }
    }
    marketplaceOffer {
      category
      offerId
    }
  }
`)

export const isOfferBlock = (block: unknown): block is ResultOf<typeof offerBlockFragment> => {
  return isBlockKind(block, offerBlockTypename, offerBlockType)
}

export const isOfferBlockWithNonNullableMarketplaceOffer = (
  block: unknown,
): block is ResultOf<typeof offerBlockFragment> & {
  marketplaceOffer: NonNullable<ResultOf<typeof offerBlockFragment>['marketplaceOffer']>
} => {
  return isOfferBlock(block) && !!block.marketplaceOffer
}

type OfferBlockProps = {
  data: FragmentType<typeof offerBlockFragment>
  isNested?: boolean
}

const renderOfferComponent = (offer: Offer) => {
  if (isCreditCardOffer(offer) || isPersonalCreditCardOffer(offer)) {
    return <CreditCardOffer offer={offer.data} />
  }

  if (isLoanOffer(offer)) {
    return <LoanOffer offer={offer.data} />
  }

  if (isProductOptionOffer(offer)) {
    return <ProductOffer offer={offer.data} />
  }

  // this should never happen
  return <PreviewWarningBlock>Could not determine offer type for {offer.category}</PreviewWarningBlock>
}

export const OfferBlock = ({ data, isNested }: OfferBlockProps) => {
  const { marketplaceOffer, invertBackground, section, ctas } = getFragmentData(offerBlockFragment, data)
  const offerFromProvider = useOffer(marketplaceOffer?.category ?? '', marketplaceOffer?.offerId ?? '')

  if (!offerFromProvider) {
    return (
      <PreviewWarningBlock>
        Unable to load offer {marketplaceOffer?.category} {marketplaceOffer?.offerId}
      </PreviewWarningBlock>
    )
  }

  return (
    <BlockSection
      invertBackground={invertBackground}
      isNested={isNested}
      section={section}
      disableDefaultPadding
      className="pb-300"
    >
      <SectionHeader data={section} links={ctas} />
      <Gutter>{renderOfferComponent(offerFromProvider)}</Gutter>
    </BlockSection>
  )
}
